import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Target, DollarSign, Truck, Users, Clock, TrendingUp,
  Loader2, RefreshCw, CheckCircle, AlertTriangle, ArrowUpRight, ArrowDownRight,
  Wallet, BarChart3
} from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => {
  if (!v && v !== 0) return '0';
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}Md`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return v.toLocaleString('fr-FR');
};

const getProgressColor = (percent) => {
  if (!percent || percent <= 0) return { bg: 'from-slate-50 to-blue-50', border: 'border-blue-200', text: 'text-blue-700', accent: 'text-blue-600', bar: 'bg-blue-400', icon: 'text-blue-400', badge: 'bg-blue-100 text-blue-700' };
  if (percent < 50) return { bg: 'from-cyan-50 to-sky-50', border: 'border-cyan-200', text: 'text-cyan-700', accent: 'text-cyan-600', bar: 'bg-cyan-500', icon: 'text-cyan-500', badge: 'bg-cyan-100 text-cyan-700' };
  if (percent < 80) return { bg: 'from-amber-50 to-amber-100', border: 'border-amber-200', text: 'text-amber-700', accent: 'text-amber-600', bar: 'bg-amber-500', icon: 'text-amber-500', badge: 'bg-amber-100 text-amber-700' };
  return { bg: 'from-green-50 to-green-100', border: 'border-green-200', text: 'text-green-700', accent: 'text-green-600', bar: 'bg-green-500', icon: 'text-green-500', badge: 'bg-green-100 text-green-700' };
};

const ObjectiveCard = ({ title, actual, target, unit, icon: Icon, percent, subtitle, colorOverride }) => {
  const pct = percent ?? (target > 0 ? Math.min((actual / target) * 100, 999) : 0);
  const colors = colorOverride || getProgressColor(pct);
  const displayValue = unit === 'FCFA' ? fmt(actual) : actual;
  const displayTarget = unit === 'FCFA' ? fmt(target) : target;

  return (
    <Card className={`bg-gradient-to-br ${colors.bg} ${colors.border} overflow-hidden`} data-testid={`obj-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <p className={`text-xs font-medium ${colors.accent}`}>{title}</p>
          <Icon className={`h-6 w-6 ${colors.icon}`} />
        </div>
        <p className={`text-2xl font-bold ${colors.text}`}>
          {displayValue}
        </p>
        {target > 0 && (
          <p className="text-xs text-gray-500 mt-0.5">
            / {displayTarget} {unit !== 'FCFA' && unit !== '%' ? unit : ''}
          </p>
        )}
        {/* Progress bar */}
        {target > 0 && (
          <div className="mt-3">
            <div className="w-full bg-white/60 rounded-full h-2 overflow-hidden">
              <div
                className={`h-full rounded-full ${colors.bar} transition-all duration-700`}
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
            <div className="flex justify-between items-center mt-1">
              <span className={`text-xs font-semibold ${colors.text}`}>{Math.round(pct)}%</span>
              {subtitle && <span className="text-xs text-gray-400">{subtitle}</span>}
            </div>
          </div>
        )}
        {!target && subtitle && (
          <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
};

const InfoCard = ({ title, value, subtitle, icon: Icon, color }) => {
  const colorMap = {
    emerald: { bg: 'from-emerald-50 to-emerald-100', border: 'border-emerald-200', text: 'text-emerald-700', icon: 'text-emerald-500' },
    cyan: { bg: 'from-cyan-50 to-cyan-100', border: 'border-cyan-200', text: 'text-cyan-700', icon: 'text-cyan-500' },
    indigo: { bg: 'from-indigo-50 to-indigo-100', border: 'border-indigo-200', text: 'text-indigo-700', icon: 'text-indigo-500' },
  };
  const c = colorMap[color] || colorMap.emerald;

  return (
    <Card className={`bg-gradient-to-br ${c.bg} ${c.border}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <p className={`text-xs font-medium ${c.text.replace('700', '600')}`}>{title}</p>
          <Icon className={`h-6 w-6 ${c.icon}`} />
        </div>
        <p className={`text-2xl font-bold ${c.text}`}>{value}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </CardContent>
    </Card>
  );
};

const ProgressBarRow = ({ label, actual, target, unit, icon: Icon }) => {
  const pct = target > 0 ? Math.min((actual / target) * 100, 100) : 0;
  const colors = getProgressColor(pct);
  const displayActual = unit === 'FCFA' ? fmt(actual) : actual;
  const displayTarget = unit === 'FCFA' ? fmt(target) : target;

  return (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2 w-40 flex-shrink-0">
        <Icon className={`h-4 w-4 ${colors.icon}`} />
        <span className="text-sm font-medium text-gray-700">{label}</span>
      </div>
      <div className="flex-1">
        <div className="w-full bg-gray-100 rounded-full h-5 overflow-hidden">
          <div
            className={`h-full rounded-full ${colors.bar} flex items-center justify-end pr-2 transition-all duration-700`}
            style={{ width: `${Math.max(pct, 8)}%` }}
          >
            <span className="text-[10px] font-bold text-white">{Math.round(pct)}%</span>
          </div>
        </div>
      </div>
      <div className="text-right w-32 flex-shrink-0">
        <span className="text-sm font-mono font-semibold text-gray-800">{displayActual}</span>
        <span className="text-xs text-gray-400"> / {displayTarget}</span>
      </div>
    </div>
  );
};

const ObjectivesKPIDashboard = () => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const headers = { Authorization: 'Bearer cookie-auth' };

  const fetchProgress = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/admin/objectives/progress`, { headers });
      setProgress(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!progress) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Target className="h-12 w-12 mx-auto mb-3 text-gray-300" />
        <p className="font-medium">Aucun objectif configuré</p>
        <p className="text-sm mt-1">Configurez vos objectifs dans Paramètres &gt; Objectifs</p>
      </div>
    );
  }

  const { arr, revenue, operations, collection, new_clients } = progress;

  // Check if any objectives are configured
  const hasRevenueTargets = revenue?.monthly?.target > 0 || revenue?.quarterly?.target > 0 || revenue?.yearly?.target > 0;
  const hasOpsTargets = operations?.monthly?.target > 0 || operations?.weekly?.target > 0;
  const hasCollectionTargets = collection?.rate?.target > 0;
  const hasClientTargets = new_clients?.monthly?.target > 0;
  const hasAnyTarget = hasRevenueTargets || hasOpsTargets || hasCollectionTargets || hasClientTargets;

  return (
    <div className="space-y-6" data-testid="objectives-kpi-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5 text-violet-500" />
          <h2 className="text-lg font-semibold text-gray-800">Objectifs Business</h2>
          <Badge variant="outline" className="text-xs">Mois en cours</Badge>
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchProgress(true)} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {!hasAnyTarget && (
        <Card className="border-dashed border-2 border-gray-200 bg-gray-50/50">
          <CardContent className="p-8 text-center">
            <Target className="h-10 w-10 mx-auto mb-3 text-gray-300" />
            <p className="font-medium text-gray-600">Aucun objectif n'est encore configuré</p>
            <p className="text-sm text-gray-400 mt-1">
              Allez dans <strong>Paramètres &gt; Objectifs</strong> pour définir vos objectifs de CA, opérations, recouvrement et acquisition clients.
            </p>
          </CardContent>
        </Card>
      )}

      {/* ARR & Revenue Collection Card */}
      {arr && (
        <Card className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden" data-testid="arr-card">
          <CardContent className="p-5">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-violet-50 rounded-lg">
                  <TrendingUp className="h-4 w-4 text-violet-600" />
                </div>
                <p className="text-sm font-bold text-slate-800 uppercase tracking-wide">Revenus Récurrents</p>
              </div>
              <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-500 border-slate-200">{arr?.contracts?.length || 0} contrat(s)</Badge>
            </div>

            {/* MRR / ARR Metrics */}
            <div className="grid grid-cols-2 gap-4 mb-5">
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-100">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">MRR / Mois</p>
                <p className="text-2xl font-bold text-slate-900 font-mono tracking-tight">{fmt(arr?.totalMonthly)}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">FCFA / mois</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-100">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">ARR / An</p>
                <p className="text-2xl font-bold text-slate-900 font-mono tracking-tight">{fmt(arr?.totalAnnual)}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">FCFA / an</p>
              </div>
            </div>

            {/* Revenue Collection Progress Bar */}
            {progress?.revenueCollection && (() => {
              const rc = progress.revenueCollection;
              const paidPct = rc.paidPct || 0;
              const invoicedPct = rc.invoicedPct || 0;
              return (
                <div className="mb-5">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Recouvrement CA global</p>
                  <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden flex">
                    {paidPct > 0 && (
                      <div className="h-full bg-violet-600 transition-all duration-1000" style={{ width: `${paidPct}%` }} />
                    )}
                    {invoicedPct > 0 && (
                      <div className="h-full bg-amber-400 transition-all duration-1000" style={{ width: `${invoicedPct}%` }} />
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-4 text-xs">
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-violet-600 inline-block" />
                        <span className="font-medium text-violet-700">Encaissé {fmt(rc.paid)}</span>
                        <span className="text-slate-400">({paidPct}%)</span>
                      </span>
                      {invoicedPct > 0 && (
                        <span className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
                          <span className="font-medium text-amber-600">Facturé {fmt(rc.invoicedNotPaid)}</span>
                          <span className="text-slate-400">({invoicedPct}%)</span>
                        </span>
                      )}
                      {rc.notInvoiced > 0 && (
                        <span className="flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-slate-200 inline-block" />
                          <span className="font-medium text-slate-500">Non facturé {fmt(rc.notInvoiced)}</span>
                        </span>
                      )}
                    </div>
                    <span className="text-xs font-mono text-slate-500">Total: {fmt(rc.totalCA)}</span>
                  </div>
                </div>
              );
            })()}

            {/* Contract List */}
            {arr?.contracts?.length > 0 && (
              <div className="border-t border-slate-100 pt-4 space-y-1">
                {arr.contracts.map(c => (
                  <div key={c.contract} className="flex items-center justify-between py-2 px-3 rounded hover:bg-slate-50 transition-colors group">
                    <span className="text-xs font-medium text-slate-700 group-hover:text-slate-900 truncate mr-2">{c.client}</span>
                    <div className="flex items-center gap-3 flex-shrink-0 font-mono text-xs text-slate-600">
                      <span>{fmt(c.monthlyRevenue)}<span className="text-slate-400">/mois</span></span>
                      <span className="text-slate-200">|</span>
                      <span>{fmt(c.annualRevenue)}<span className="text-slate-400">/an</span></span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Financial Objectives */}
      {hasRevenueTargets && (
        <>
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-emerald-500" />
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Objectifs Financiers</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {revenue?.monthly?.target > 0 && (
              <ObjectiveCard
                title="CA Mensuel"
                actual={revenue.monthly.actual}
                target={revenue.monthly.target}
                unit="FCFA"
                icon={DollarSign}
                percent={revenue.monthly.percent}
              />
            )}
            {revenue?.quarterly?.target > 0 && (
              <ObjectiveCard
                title="CA Trimestriel"
                actual={revenue.quarterly.actual}
                target={revenue.quarterly.target}
                unit="FCFA"
                icon={BarChart3}
                percent={revenue.quarterly.percent}
              />
            )}
            {revenue?.yearly?.target > 0 && (
              <ObjectiveCard
                title="CA Annuel"
                actual={revenue.yearly.actual}
                target={revenue.yearly.target}
                unit="FCFA"
                icon={TrendingUp}
                percent={revenue.yearly.percent}
              />
            )}
          </div>
        </>
      )}

      {/* Operational Objectives */}
      {hasOpsTargets && (
        <>
          <div className="flex items-center gap-2 mt-2">
            <Truck className="h-4 w-4 text-blue-500" />
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Objectifs Opérationnels</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {operations?.monthly?.target > 0 && (
              <ObjectiveCard
                title="Ops / Mois"
                actual={operations.monthly.actual}
                target={operations.monthly.target}
                unit="ops"
                icon={Truck}
                percent={operations.monthly.percent}
              />
            )}
            {operations?.weekly?.target > 0 && (
              <ObjectiveCard
                title="Ops / Semaine"
                actual={operations.weekly.actual}
                target={operations.weekly.target}
                unit="ops"
                icon={Truck}
                percent={operations.weekly.percent}
              />
            )}
            {hasCollectionTargets && (
              <ObjectiveCard
                title="Taux Recouvrement"
                actual={`${collection.rate.actual}%`}
                target={collection.rate.target}
                unit="%"
                icon={Wallet}
                percent={collection.rate.percent}
                subtitle={`Objectif: ${collection.rate.target}%`}
              />
            )}
            {collection?.dso && (
              <ObjectiveCard
                title="DSO (Délai Encaissement)"
                actual={`${collection.dso.actual}j`}
                target={collection.dso.target}
                unit="jours"
                icon={Clock}
                percent={collection.dso.on_target ? 100 : Math.max(0, 100 - ((collection.dso.actual - collection.dso.target) / collection.dso.target) * 100)}
                subtitle={collection.dso.on_target ? 'Dans l\'objectif' : 'Hors objectif'}
                colorOverride={collection.dso.on_target
                  ? { bg: 'from-green-50 to-green-100', border: 'border-green-200', text: 'text-green-700', accent: 'text-green-600', bar: 'bg-green-500', icon: 'text-green-500' }
                  : { bg: 'from-amber-50 to-amber-100', border: 'border-amber-200', text: 'text-amber-700', accent: 'text-amber-600', bar: 'bg-amber-500', icon: 'text-amber-500' }
                }
              />
            )}
          </div>
        </>
      )}

      {/* Client Objectives */}
      {hasClientTargets && (
        <>
          <div className="flex items-center gap-2 mt-2">
            <Users className="h-4 w-4 text-purple-500" />
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Acquisition Clients</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {new_clients?.monthly?.target > 0 && (
              <ObjectiveCard
                title="Nouveaux Clients / Mois"
                actual={new_clients.monthly.actual}
                target={new_clients.monthly.target}
                unit="clients"
                icon={Users}
                percent={new_clients.monthly.percent}
              />
            )}
          </div>
        </>
      )}

      {/* Summary Progress Bars */}
      {hasAnyTarget && (
        <>
          <div className="flex items-center gap-2 mt-4">
            <BarChart3 className="h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Vue synthétique</h3>
          </div>
          <Card>
            <CardContent className="p-5 space-y-4">
              {revenue?.monthly?.target > 0 && (
                <ProgressBarRow
                  label="CA Mensuel"
                  actual={revenue.monthly.actual}
                  target={revenue.monthly.target}
                  unit="FCFA"
                  icon={DollarSign}
                />
              )}
              {operations?.monthly?.target > 0 && (
                <ProgressBarRow
                  label="Ops / Mois"
                  actual={operations.monthly.actual}
                  target={operations.monthly.target}
                  unit="ops"
                  icon={Truck}
                />
              )}
              {hasCollectionTargets && (
                <ProgressBarRow
                  label="Recouvrement"
                  actual={collection.rate.actual}
                  target={collection.rate.target}
                  unit="%"
                  icon={Wallet}
                />
              )}
              {collection?.dso?.target > 0 && (
                <ProgressBarRow
                  label="DSO"
                  actual={collection.dso.actual}
                  target={collection.dso.target}
                  unit="jours"
                  icon={Clock}
                />
              )}
              {new_clients?.monthly?.target > 0 && (
                <ProgressBarRow
                  label="Nvx Clients"
                  actual={new_clients.monthly.actual}
                  target={new_clients.monthly.target}
                  unit="clients"
                  icon={Users}
                />
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ObjectivesKPIDashboard;
